import type { FilterSchema } from '@/shared/components/filter/validation'
import type { ProductsByEntityRequestModel } from '@/shared/generated'

import { useNavigate } from '@tanstack/react-router'
import { filter, map, pipe, propEq, propOr, sum } from 'lodash/fp'
import { ChevronRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useProductsCountByEngineQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import { Operators } from '@/shared/components/query-builder'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { ampli } from '@/shared/lib/ampli'
import { cn } from '@/shared/lib/utils'
import { formatNumber } from '@/shared/tools/locale'
import { getFiltersQueryString } from '@/shared/tools/location'

import { Widget, WidgetContent, WidgetHeader } from '../../Widget'

const getUnassignedItems = pipe([
	filter(propEq('pricing_campaign_engine', 'unassigned')),
	map(propOr(0, 'product_count')),
	sum,
])

export default function NumericUnasignedItemsWidget({
	className,
	commonFilters,
}: {
	className?: string
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()
	const currency = useSelectedCurrency()

	const navigate = useNavigate()
	const commonQueryParams = {
		filters: [...commonFilters],
		currency,
	}
	const { isFetching, data } = useProductsCountByEngineQuery<number>(
		commonQueryParams as ProductsByEntityRequestModel,
		{
			select: getUnassignedItems,
		}
	)

	return (
		<Widget
			className={cn(
				'relative flex flex-auto cursor-pointer flex-col transition-colors hover:border-primary hover:bg-primary/10',
				className
			)}
			onClick={() => {
				ampli.dashboardBiProceedToUnassignedList()
				navigate({
					to: `/p/products${getFiltersQueryString([
						{
							name: 'pricing_campaign_id',
							operation: Operators.IS_EMPTY as const,
							value: '1',
						},
					])}`,
				})
			}}
		>
			<WidgetHeader
				title={t('general_new_items')}
				tooltip={t('unassigned_items.tooltip')}
				subtitle={t('items_without_pc', { defaultValue: 'items without PC' })}
			/>
			<WidgetContent className='truncate'>
				<div className='mt-auto w-full'>
					{isFetching ? (
						<Skeleton className='col-span-2 h-10 w-32' />
					) : (
						<div className='flex items-center justify-between'>
							<span className='truncate text-3xl font-semibold'>
								{formatNumber(data!)}
							</span>
							<ChevronRightIcon className='text-muted-foreground dark:text-white' />
						</div>
					)}
				</div>
			</WidgetContent>
		</Widget>
	)
}
